/**
 * Knowledge Base API - matches backend endpoints.
 * Endpoints:
 *   GET    /bot/api/knowledge-base                → fetch all sources (products, pages, promotions, custom)
 *   POST   /bot/api/knowledge-base/upload         → upload files (FormData)
 *   POST   /bot/api/knowledge-base/create-doc     → create editor document (JSON)
 *   POST   /bot/api/knowledge-base/sync           → sync from Shopify
 *   PUT    /bot/api/knowledge-base/:id/rename     → rename a source
 *   PUT    /bot/api/knowledge-base/:id/exclude    → exclude source from training
 *   PUT    /bot/api/knowledge-base/:id/include    → re-include source in training
 *   DELETE /bot/api/knowledge-base/:id            → delete a custom source
 *   POST   /bot/api/knowledge-base/bulk-exclude   → bulk exclude
 *   POST   /bot/api/knowledge-base/bulk-include   → bulk include
 *   POST   /bot/api/knowledge-base/bulk-delete    → bulk delete
 * Uses global auth: Bearer token from auth.js for verification.
 */

import { getAuthHeaders } from './auth';
import { getBaseUrl, parseJson, buildUrl } from './utils';

const KB_PATH = 'bot/api/knowledge-base';

/**
 * Fetch a page of knowledge base sources.
 * GET /bot/api/knowledge-base?category=...&page=...&page_size=...&q=...&types=...&statuses=...&sort=...
 *
 * @param {object} [opts]
 * @param {string} [opts.category] - 'products' | 'pages' | 'promotions' | 'custom' | 'website'.
 *                                   Omit for the All Sources view.
 * @param {number} [opts.page=1]
 * @param {number} [opts.pageSize=50]
 * @param {string} [opts.q] - search query (case-insensitive substring match on name).
 * @param {boolean} [opts.idsOnly=false] - when true, returns { ids: [...] } only.
 * @param {string[]} [opts.types] - filter to only these source_type values (OR-combined).
 * @param {string[]} [opts.statuses] - filter to only these status values (OR-combined).
 * @param {string} [opts.sort] - sort spec: 'name:asc' | 'name:desc' | 'updated:asc' | 'updated:desc'.
 *                               Defaults to 'updated:desc' server-side.
 * @returns {Promise<object>} { items, total, global_total, trained_count, page, page_size, facets }
 */
export async function getKnowledgeBase(opts = {}) {
  const {
    category,
    page = 1,
    pageSize = 50,
    q = '',
    idsOnly = false,
    types = [],
    statuses = [],
    sort = '',
  } = opts;
  const params = new URLSearchParams();
  if (category) params.set('category', category);
  if (page) params.set('page', String(page));
  if (pageSize) params.set('page_size', String(pageSize));
  if (q) params.set('q', q);
  if (idsOnly) params.set('ids_only', 'true');
  if (Array.isArray(types) && types.length) params.set('types', types.join(','));
  if (Array.isArray(statuses) && statuses.length) params.set('statuses', statuses.join(','));
  if (sort) params.set('sort', sort);

  const qs = params.toString();
  const path = qs ? `${KB_PATH}?${qs}` : KB_PATH;
  const url = buildUrl(getBaseUrl(), path);
  const res = await fetch(url, {
    method: 'GET',
    headers: { Accept: 'application/json', ...getAuthHeaders() },
  });
  return parseJson(res);
}

/**
 * Fetch a single KB source with full content.
 * GET /bot/api/knowledge-base/:id
 * @param {number} id
 * @returns {Promise<object>}
 */
export async function getDocument(id) {
  const url = buildUrl(getBaseUrl(), `${KB_PATH}/${id}`);
  const res = await fetch(url, {
    method: 'GET',
    headers: { Accept: 'application/json', ...getAuthHeaders() },
  });
  return parseJson(res);
}

/**
 * Update a custom editor document's title and/or content.
 * PUT /bot/api/knowledge-base/:id/update-doc
 * @param {number} id
 * @param {{ title?: string, content?: string }} updates
 * @returns {Promise<object>}
 */
export async function updateDocument(id, updates) {
  const url = buildUrl(getBaseUrl(), `${KB_PATH}/${id}/update-doc`);
  const res = await fetch(url, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      ...getAuthHeaders(),
    },
    body: JSON.stringify(updates),
  });
  return parseJson(res);
}

/**
 * View an uploaded custom source file inline in a new browser tab.
 * GET /bot/api/knowledge-base/:id/download?disposition=inline
 * The presigned URL is served with Content-Disposition: inline and the correct
 * Content-Type, so PDFs, images and text render in-tab instead of downloading.
 * (Word/Excel still download — browsers cannot render them inline.)
 * @param {number} id
 */
export async function viewSource(id) {
  const url = buildUrl(getBaseUrl(), `${KB_PATH}/${id}/download`) + '?disposition=inline';
  const res = await fetch(url, {
    method: 'GET',
    headers: { Accept: 'application/json', ...getAuthHeaders() },
  });
  const data = await parseJson(res);
  if (data?.url) {
    window.open(data.url, '_blank', 'noopener,noreferrer');
  } else {
    throw new Error('Failed to get document URL');
  }
}

/**
 * Download an uploaded custom source file.
 * GET /bot/api/knowledge-base/:id/download?disposition=attachment
 * The presigned URL is served with Content-Disposition: attachment, so the
 * browser saves the file instead of opening it.
 * @param {number} id
 */
export async function downloadSource(id) {
  const url = buildUrl(getBaseUrl(), `${KB_PATH}/${id}/download`) + '?disposition=attachment';
  const res = await fetch(url, {
    method: 'GET',
    headers: { Accept: 'application/json', ...getAuthHeaders() },
  });
  const data = await parseJson(res);
  if (data?.url) {
    const a = document.createElement('a');
    a.href = data.url;
    a.download = data.name || '';
    document.body.appendChild(a);
    a.click();
    a.remove();
  } else {
    throw new Error('Failed to get document URL');
  }
}

/**
 * Upload files to knowledge base.
 * POST /bot/api/knowledge-base/upload
 * @param {Array<{ file: File, title: string }>} files
 * @returns {Promise<object>}
 */
export async function uploadFiles(files) {
  const url = buildUrl(getBaseUrl(), `${KB_PATH}/upload`);
  const formData = new FormData();
  files.forEach((f, i) => {
    formData.append('files', f.file);
    formData.append(`titles[${i}]`, f.title);
  });
  const res = await fetch(url, {
    method: 'POST',
    headers: { Accept: 'application/json', ...getAuthHeaders() },
    body: formData,
  });
  return parseJson(res);
}

/**
 * Create a document via the editor.
 * POST /bot/api/knowledge-base/create-doc
 *
 * Phase 2, PR 2: optional `kind` ('corpus' default | 'template') and `slug`
 * (required when kind='template'). Templated Messages are NEVER chunked
 * or embedded — they're consumed verbatim by Playbook `kb_template_ref`
 * renderer. Slug must match ^[a-z0-9_-]{2,64}$ and be unique per company.
 *
 * @param {{ title: string, content: string, kind?: string, slug?: string }} doc
 * @returns {Promise<object>}
 */
export async function createDocument(doc) {
  const url = buildUrl(getBaseUrl(), `${KB_PATH}/create-doc`);
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      ...getAuthHeaders(),
    },
    body: JSON.stringify(doc),
  });
  return parseJson(res);
}

/**
 * Sync data from Shopify.
 * POST /bot/api/knowledge-base/sync
 * @param {string} tab - which tab to sync: 'products' | 'pages' | 'promotions'
 * @returns {Promise<object>}
 */
export async function syncFromShopify(tab) {
  const url = buildUrl(getBaseUrl(), `${KB_PATH}/sync`);
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      ...getAuthHeaders(),
    },
    body: JSON.stringify({ tab }),
  });
  return parseJson(res);
}

/**
 * Rename a source.
 * PUT /bot/api/knowledge-base/:id/rename
 */
export async function renameSource(id, name) {
  const url = buildUrl(getBaseUrl(), `${KB_PATH}/${id}/rename`);
  const res = await fetch(url, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      ...getAuthHeaders(),
    },
    body: JSON.stringify({ name }),
  });
  return parseJson(res);
}

/**
 * Exclude a source from training.
 * PUT /bot/api/knowledge-base/:id/exclude
 */
export async function excludeSource(id) {
  const url = buildUrl(getBaseUrl(), `${KB_PATH}/${id}/exclude`);
  const res = await fetch(url, {
    method: 'PUT',
    headers: { Accept: 'application/json', ...getAuthHeaders() },
  });
  return parseJson(res);
}

/**
 * Re-include a source in training.
 * PUT /bot/api/knowledge-base/:id/include
 */
export async function includeSource(id) {
  const url = buildUrl(getBaseUrl(), `${KB_PATH}/${id}/include`);
  const res = await fetch(url, {
    method: 'PUT',
    headers: { Accept: 'application/json', ...getAuthHeaders() },
  });
  return parseJson(res);
}

/**
 * Delete a custom source.
 * DELETE /bot/api/knowledge-base/:id
 */
export async function deleteSource(id) {
  const url = buildUrl(getBaseUrl(), `${KB_PATH}/${id}`);
  const res = await fetch(url, {
    method: 'DELETE',
    headers: { Accept: 'application/json', ...getAuthHeaders() },
  });
  return parseJson(res);
}

/**
 * Bulk exclude sources.
 * POST /bot/api/knowledge-base/bulk-exclude
 */
export async function bulkExclude(ids) {
  const url = buildUrl(getBaseUrl(), `${KB_PATH}/bulk-exclude`);
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      ...getAuthHeaders(),
    },
    body: JSON.stringify({ ids }),
  });
  return parseJson(res);
}

/**
 * Bulk include sources.
 * POST /bot/api/knowledge-base/bulk-include
 */
export async function bulkInclude(ids) {
  const url = buildUrl(getBaseUrl(), `${KB_PATH}/bulk-include`);
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      ...getAuthHeaders(),
    },
    body: JSON.stringify({ ids }),
  });
  return parseJson(res);
}

/**
 * Sync all knowledge base sources.
 * POST /bot/api/knowledge-base/sync-all
 * @returns {Promise<object>}
 */
export async function syncAll() {
  const url = buildUrl(getBaseUrl(), `${KB_PATH}/sync-all`);
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      ...getAuthHeaders(),
    },
  });
  return parseJson(res);
}

/**
 * Check if initial knowledge base sync has been completed.
 * GET /bot/api/shopify/initial-sync-status
 * @returns {Promise<{ synced: boolean }>}
 */
export async function getInitialSyncStatus() {
  const url = buildUrl(getBaseUrl(), 'bot/api/shopify/initial-sync-status');
  const res = await fetch(url, {
    method: 'GET',
    headers: { Accept: 'application/json', ...getAuthHeaders() },
  });
  return parseJson(res);
}

/**
 * Bulk delete custom sources.
 * POST /bot/api/knowledge-base/bulk-delete
 */
export async function bulkDelete(ids) {
  const url = buildUrl(getBaseUrl(), `${KB_PATH}/bulk-delete`);
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      ...getAuthHeaders(),
    },
    body: JSON.stringify({ ids }),
  });
  return parseJson(res);
}


// ── Website Crawling ─────────────────────────────────────────────────────────

/**
 * Start crawling a website.
 * POST /bot/api/knowledge-base/crawl-website
 * @param {string} url - The website URL to crawl
 * @param {number} maxPages - Maximum pages to crawl (default 50)
 * @returns {Promise<object>} The created crawl job
 */
export async function crawlWebsite(url, maxPages = 50) {
  const apiUrl = buildUrl(getBaseUrl(), `${KB_PATH}/crawl-website`);
  const res = await fetch(apiUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      ...getAuthHeaders(),
    },
    body: JSON.stringify({ url, max_pages: maxPages }),
  });
  return parseJson(res);
}

/**
 * List all crawl jobs for the current company.
 * GET /bot/api/knowledge-base/crawl-jobs
 * @returns {Promise<Array>}
 */
export async function getCrawlJobs() {
  const apiUrl = buildUrl(getBaseUrl(), `${KB_PATH}/crawl-jobs`);
  const res = await fetch(apiUrl, {
    method: 'GET',
    headers: { Accept: 'application/json', ...getAuthHeaders() },
  });
  return parseJson(res);
}

/**
 * Get a single crawl job with progress.
 * GET /bot/api/knowledge-base/crawl-jobs/:id
 * @param {number} id
 * @returns {Promise<object>}
 */
export async function getCrawlJobStatus(id) {
  const apiUrl = buildUrl(getBaseUrl(), `${KB_PATH}/crawl-jobs/${id}`);
  const res = await fetch(apiUrl, {
    method: 'GET',
    headers: { Accept: 'application/json', ...getAuthHeaders() },
  });
  return parseJson(res);
}

/**
 * Re-crawl an existing crawl job.
 * POST /bot/api/knowledge-base/crawl-jobs/:id/recrawl
 * @param {number} id
 * @returns {Promise<object>}
 */
export async function recrawlJob(id) {
  const apiUrl = buildUrl(getBaseUrl(), `${KB_PATH}/crawl-jobs/${id}/recrawl`);
  const res = await fetch(apiUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      ...getAuthHeaders(),
    },
  });
  return parseJson(res);
}

/**
 * Delete a crawl job and all its pages.
 * DELETE /bot/api/knowledge-base/crawl-jobs/:id
 * @param {number} id
 * @returns {Promise<object>}
 */
export async function deleteCrawlJob(id) {
  const apiUrl = buildUrl(getBaseUrl(), `${KB_PATH}/crawl-jobs/${id}`);
  const res = await fetch(apiUrl, {
    method: 'DELETE',
    headers: { Accept: 'application/json', ...getAuthHeaders() },
  });
  return parseJson(res);
}


// ── Domain Verification ──────────────────────────────────────────────────────

/**
 * Initiate domain verification — generates a token.
 * POST /bot/api/knowledge-base/verify-domain
 * @param {string} domain - The domain to verify (e.g. "example.com")
 * @returns {Promise<object>} { domain, token, verified, alreadyVerified? }
 */
export async function verifyDomain(domain) {
  const apiUrl = buildUrl(getBaseUrl(), `${KB_PATH}/verify-domain`);
  const res = await fetch(apiUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      ...getAuthHeaders(),
    },
    body: JSON.stringify({ domain }),
  });
  return parseJson(res);
}

/**
 * Check if a domain verification token is present on the site.
 * POST /bot/api/knowledge-base/verify-domain/check
 * @param {string} domain
 * @returns {Promise<object>} { verified, domain, token }
 */
export async function checkDomainVerification(domain) {
  const apiUrl = buildUrl(getBaseUrl(), `${KB_PATH}/verify-domain/check`);
  const res = await fetch(apiUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      ...getAuthHeaders(),
    },
    body: JSON.stringify({ domain }),
  });
  return parseJson(res);
}

/**
 * List all verified domains for the current company.
 * GET /bot/api/knowledge-base/verified-domains
 * @returns {Promise<Array>}
 */
export async function getVerifiedDomains() {
  const apiUrl = buildUrl(getBaseUrl(), `${KB_PATH}/verified-domains`);
  const res = await fetch(apiUrl, {
    method: 'GET',
    headers: { Accept: 'application/json', ...getAuthHeaders() },
  });
  return parseJson(res);
}
